import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon, SearchIcon } from 'lucide-react';
import { CountryRow } from '../../types/census';
import { formatCompact, formatInt, formatPct } from '../../utils/format';
import { agentsPerCapitaFor } from '../../utils/estimates';
import { useScenario } from '../../contexts/ScenarioContext';
import { SourceRef } from '../SourceRef';
type SortKey = 'rank' | 'name' | 'population' | 'internetPct' | 'internetUsers' | 'medianAge' | 'mobile' | 'endpoints' | 'agentsPerCapita';
type RankedRow = CountryRow & {
  rank: number;
  scenarioAgents: number | null;
};
interface Column {
  key: SortKey;
  label: string;
  numeric: boolean;
  sourceId?: string;
  estimate?: boolean;
}
const COLUMNS: Column[] = [{
  key: 'rank',
  label: 'Rank',
  numeric: true
}, {
  key: 'name',
  label: 'Country',
  numeric: false
}, {
  key: 'population',
  label: 'Population',
  numeric: true,
  sourceId: 'restcountries'
}, {
  key: 'internetPct',
  label: 'Internet %',
  numeric: true,
  sourceId: 'wbInternet'
}, {
  key: 'internetUsers',
  label: 'Internet users',
  numeric: true,
  sourceId: 'wbInternet'
}, {
  key: 'medianAge',
  label: 'Median age',
  numeric: true,
  sourceId: 'unwpp'
}, {
  key: 'mobile',
  label: 'Mobile / 100',
  numeric: true,
  sourceId: 'wbMobile'
}, {
  key: 'endpoints',
  label: 'Assistant endpoints',
  numeric: true,
  sourceId: 'censusModel',
  estimate: true
}, {
  key: 'agentsPerCapita',
  label: 'Agents / capita',
  numeric: true,
  sourceId: 'censusModel',
  estimate: true
}];
const getters: Record<SortKey, (r: RankedRow) => number | string | null> = {
  rank: r => r.rank,
  name: r => r.name,
  population: r => r.population,
  internetPct: r => r.internetPct?.value ?? null,
  internetUsers: r => r.internetUsers,
  medianAge: r => r.medianAge,
  mobile: r => r.mobilePer100?.value ?? null,
  endpoints: r => r.endpoints,
  agentsPerCapita: r => r.scenarioAgents
};
const PAGE = 50;
export function CountryTable({
  rows
}: {
  rows: CountryRow[];
}) {
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const {
    scenarioId,
    scenario
  } = useScenario();
  const ranked: RankedRow[] = useMemo(() => [...rows].sort((a, b) => b.population - a.population).map((r, i) => ({
    ...r,
    rank: i + 1,
    scenarioAgents: agentsPerCapitaFor(r, scenarioId)
  })), [rows, scenarioId]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? ranked.filter(r => r.name.toLowerCase().includes(q) || r.cca3.toLowerCase().includes(q) || r.region.toLowerCase().includes(q)) : ranked;
    const get = getters[sortKey];
    return [...list].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'string' ? va.localeCompare(String(vb)) : (va as number) - (vb as number);
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [ranked, query, sortKey, dir]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const current = Math.min(page, pages - 1);
  const visible = showAll ? filtered : filtered.slice(current * PAGE, current * PAGE + PAGE);
  const sortBy = (key: SortKey) => {
    if (key === sortKey) setDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortKey(key);
      setDir(key === 'name' || key === 'rank' ? 'asc' : 'desc');
    }
    setPage(0);
  };
  return <div data-id="element-120" data-mp="39oqfnsya3rv1">
      <div className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-121" data-mp="39nx48eodke6k">
        <label className="relative block w-full sm:max-w-xs" data-id="element-122" data-mp="szb6ssldawh7">
          <span className="sr-only" data-id="element-123" data-mp="oyr8vmsyzou0">Search countries</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-124" data-mp="oy7ps2rl2uee" />
          <input type="search" value={query} onChange={e => {
          setQuery(e.target.value);
          setPage(0);
        }} placeholder="Search country, code or region" className="w-full rounded-md border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink placeholder:text-faint focus:border-machine focus:outline-none" data-id="element-125" data-mp="1oaj3hc3b9x63" />
        </label>
        <p className="font-mono text-[12px] text-muted" aria-live="polite" data-id="element-126" data-mp="278lnfch053x1">
          {filtered.length} of {ranked.length} countries
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line" data-id="element-127" data-mp="2c2g2g2lnwups">
        <table className="w-full min-w-[1080px] border-collapse text-[13.5px]" data-id="element-128" data-mp="2gyope263ps7i">
          <thead className="bg-surface" data-id="element-129" data-mp="2lzccwdv4fqwz">
            <tr data-id="element-130" data-mp="2lw6rb8lpb760">
              {COLUMNS.map(c => {
              const active = c.key === sortKey;
              return <th key={c.key} scope="col" aria-sort={active ? dir === 'asc' ? 'ascending' : 'descending' : 'none'} className={`whitespace-nowrap border-b border-line px-3 py-2.5 font-normal ${c.numeric ? 'text-right' : 'text-left'}`} data-id="element-131" data-mp="fak61icilgq1">
                    <button type="button" onClick={() => sortBy(c.key)} className={`inline-flex items-center gap-1 text-[12px] transition-colors duration-150 ${active ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-132" data-mp="eb9wpnx7jhu">
                      {c.label}
                      {c.estimate && <span className="text-machine" data-id="element-133" data-mp="jbvwtr74k7zt">*</span>}
                      {active ? dir === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-134" data-mp="1gr00zmegrb74" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-135" data-mp="1gs1q2usbdy7u" /> : <span className="w-3" data-id="element-136" data-mp="1gt31x1h5moqm" />}
                    </button>
                    {c.sourceId && <SourceRef id={c.sourceId} estimate={c.estimate ? {
                  low: '−15%',
                  high: '+15%'
                } : undefined} data-id="element-137" data-mp="1byyteds8q8nf" />}
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="font-mono tabular" data-id="element-138" data-mp="3b6f7hi5jj2r7">
            {visible.map(r => <tr key={r.cca3} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-139" data-mp="2whsv9pb7mr47">
                <td className="px-3 py-2 text-right text-muted" data-id="element-140" data-mp="31fueqbcjssg7">{r.rank}</td>
                <td className="px-3 py-2 font-sans" data-id="element-141" data-mp="2ngkw750vdm92">
                  <Link to={`/countries/${r.cca3}`} className="flex items-center gap-2.5 text-ink hover:text-machine" data-id="element-142" data-mp="2nk0bo8yggxb0">
                    <img src={r.flag} alt="" loading="lazy" className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover ring-1 ring-line" data-id="element-143" data-mp="2djzrylwocf6a" />
                    <span className="truncate" data-id="element-144" data-mp="2ijcgrc73vwbi">{r.name}</span>
                  </Link>
                </td>
                <td className="px-3 py-2 text-right text-ink" data-id="element-145" data-mp="bsmnfxymc9ar">{formatInt(r.population)}</td>
                <td className="px-3 py-2 text-right" data-id="element-146" data-mp="gpv5lyjrodc0">{r.internetPct ? formatPct(r.internetPct.value) : <Dash data-id="element-147" data-mp="21aa3730seks8" />}</td>
                <td className="px-3 py-2 text-right" data-id="element-148" data-mp="219i7ls76wn1y">{r.internetUsers != null ? formatCompact(r.internetUsers) : <Dash data-id="element-149" data-mp="1wbyvoyxysmd6" />}</td>
                <td className="px-3 py-2 text-right" data-id="element-150" data-mp="2az1tl6q78x11">{r.medianAge != null ? r.medianAge.toFixed(1) : <Dash data-id="element-151" data-mp="2b14oo567lgcm" />}</td>
                <td className="px-3 py-2 text-right" data-id="element-152" data-mp="267s4c78kbvpo">{r.mobilePer100 ? r.mobilePer100.value.toFixed(1) : <Dash data-id="element-153" data-mp="2jzq9k6z5l7gk" />}</td>
                <td className="px-3 py-2 text-right text-machine" data-id="element-154" data-mp="2jyouzsixi2u3">{r.endpoints != null ? formatCompact(r.endpoints) : <Dash data-id="element-155" data-mp="2ezlp65v4jsl2" />}</td>
                <td className="px-3 py-2 text-right text-machine" data-id="element-156" data-mp="2f0dljiwjpuce">
                  {r.scenarioAgents != null ? r.scenarioAgents < 0.1 ? r.scenarioAgents.toFixed(4) : r.scenarioAgents.toFixed(2) : <Dash data-id="element-157" data-mp="2p04fnjh727by" />}
                </td>
              </tr>)}
            {visible.length === 0 && <tr data-id="element-158" data-mp="zk4ybf3f9bl8">
                <td colSpan={COLUMNS.length} className="px-3 py-10 text-center font-sans text-muted" data-id="element-159" data-mp="zlptn6xk8yk5">
                  No countries match “{query}”.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-id="element-160" data-mp="dpo0ed1mkxhh">
        <p className="text-[12px] text-muted" data-id="element-161" data-mp="igwalbas4mvp">
          <span className="text-machine" data-id="element-162" data-mp="ii7ldv2n6uwb">*</span> Our estimate — agents per capita follow the active scenario (<span className="text-ink" data-id="element-163" data-mp="nfpkyuurrxrn">{scenario.label}</span>). Formula on the methodology page.
        </p>
        <div className="flex items-center gap-2" data-id="element-164" data-mp="3m092yvxwvjzo">
          {!showAll && <>
              <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)} className="rounded-md border border-line px-3 py-1.5 text-sm text-ink disabled:opacity-40" data-id="element-165" data-mp="3qzbvjgwpfxqr">
                Previous
              </button>
              <span className="font-mono text-[12px] text-muted" data-id="element-166" data-mp="1b6d5qgpsahvm">
                {current + 1} / {pages}
              </span>
              <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} className="rounded-md border border-line px-3 py-1.5 text-sm text-ink disabled:opacity-40" data-id="element-167" data-mp="1f6ng05klzuga">
                Next
              </button>
            </>}
          <button type="button" onClick={() => setShowAll(s => !s)} className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:border-machine" data-id="element-168" data-mp="2qx5rtwcdmrhm">
            {showAll ? 'Paginate (50 / page)' : 'Load all'}
          </button>
        </div>
      </div>
    </div>;
}
function Dash() {
  return <span className="text-faint" data-id="element-169" data-mp="2bzn48xbfqkbr">—</span>;
}